import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { History } from 'lucide-react';
import { t } from '../../i18n/t';
import type { Page } from '../../types/Page';
import { useRecentPages } from '../../hooks/useRecentPages';
import { resolveIcon } from './sidebarIcons';

interface SidebarRecentProps {
  pages: Page[] | null;
  // Collapsed (icons-only) sidebar → the trigger uses the collapsed-icon style;
  // pinned → it matches the header icon buttons. Either way it's just an icon
  // that opens the floating recent-screens popover.
  collapsed: boolean;
}

// "Recently visited" trigger: a single History icon that opens a floating
// popover listing the recent screens. The popover is rendered in a portal with
// fixed positioning so it escapes the sidebar's `overflow: hidden` (critical in
// collapsed mode, where it must fly out over the content area).
export function SidebarRecent({ pages, collapsed }: SidebarRecentProps) {
  const recent = useRecentPages(pages);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Physical coordinates (px from viewport top / right). Physical `right` keeps
  // positioning correct regardless of the document's RTL direction.
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(
    null,
  );
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const title = t('sidebar.recentTitle');

  const computeCoords = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 6, right: window.innerWidth - r.right });
  }, []);

  const toggle = useCallback(() => {
    if (!open) computeCoords();
    setOpen((prev) => !prev);
  }, [open, computeCoords]);

  // While open: close on outside click / Escape, and keep the popover anchored
  // if the window resizes.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const tgt = e.target as Node;
      if (popoverRef.current?.contains(tgt)) return;
      if (btnRef.current?.contains(tgt)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', computeCoords);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', computeCoords);
    };
  }, [open, computeCoords]);

  // Move focus into the menu for keyboard users once it opens.
  useEffect(() => {
    if (!open) return;
    const first = popoverRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]',
    );
    first?.focus();
  }, [open]);

  // Navigating away closes the popover.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Nothing visited yet → render nothing in both modes.
  if (recent.length === 0) return null;

  function handleSelect(url: string) {
    setOpen(false);
    navigate(url);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={collapsed ? 'sidebar__collapsed-icon' : 'sidebar__header-btn'}
        onClick={toggle}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <History size={collapsed ? 20 : 18} />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={popoverRef}
            className="sidebar__recent-popover"
            role="menu"
            aria-label={title}
            style={{ top: coords.top, right: coords.right }}
          >
            <div className="sidebar__recent-popover-title">{title}</div>
            {recent.map((entry) => {
              const Icon = resolveIcon(entry.iconName);
              return (
                <button
                  key={entry.url}
                  type="button"
                  role="menuitem"
                  className="sidebar__recent-popover-item"
                  onClick={() => handleSelect(entry.url)}
                  title={entry.displayName}
                >
                  {Icon && <Icon size={16} />}
                  <span>{entry.displayName}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
